import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { showNotification } from 'src/app/helpers/show-toast';
import { AreaService } from 'src/app/_services/area.service';
import { CityService } from 'src/app/_services/city.service';
import { PspTasksService } from 'src/app/_services/PSP/psp-tasks.service';
import { PspProgramsService } from 'src/app/_services/PSP/psp-programs.service';
import { PSPProgram } from 'src/app/_models/PSP/psp-program-model';
import { PSPPatient } from 'src/app/_models/PSP/psp-patient.model';
import { PatientSubscribeToPSP } from 'src/app/_models/PSP/psp-new-patient-sub.model';
import { FormControl, NgForm } from '@angular/forms';
import { CompanyService } from 'src/app/_services/company.service';
import { PspCompanyEnum } from '../../../enums/psp-company.enum';
import { DatePipe } from '@angular/common';
import { LookupModalService } from 'src/app/_services/PSP/modals.service';
import {
  MedicationDto,
  RocheSubscriptionData,
} from 'src/app/_models/PSP/psp-roche.model';
import {
  MedicationFormValue,
  MedicationSearchResult,
  MedicineMetadata,
} from '@components/medication-dosage-form/medication-dosage-form.component';
import { forkJoin, Observable, of } from 'rxjs';
import { FilesService } from 'src/app/_services/files/files.service';
import { PspTypeEnum } from 'src/app/enums/psp-type.enum';
import { nationalIdRegex } from 'src/app/_constants/ValidationPatterns';
@Component({
  selector: 'app-add-roche-patient',
  templateUrl: './add-roche-patient.component.html',
  styleUrls: ['./add-roche-patient.component.scss'],
})
export class AddRochePatientComponent implements OnInit {
  cities = [];
  areas = [];
  companies = [];
  programList: PSPProgram[];
  user = {} as PSPPatient;
  newPatient = {} as PatientSubscribeToPSP;
  companyId?: number;
  isPreFetched = false;
  uploadFileTypes: string = '.jpg, .jpeg, .png, .gif, .pdf';
  uploadedDocuments: File[] = [];
  PspCompanyEnum = PspCompanyEnum;
  patientDateOfBirth = '';
  passportIdToggleOn = false;
  readonly Number = Number;
  maxDocumentsToUpload = 10;
  pspCode: string = '';
  rocheTask: RocheSubscriptionData;
  programMedicationsControl = new FormControl<MedicationFormValue[]>([]);
  programMedications: MedicineMetadata[] = [];
  subscriptionDate: string;
  nationalIdRegex = nationalIdRegex;
  constructor(
    private pspTaskService: PspTasksService,
    private router: Router,
    private route: ActivatedRoute,
    private companyService: CompanyService,
    private areaService: AreaService,
    private cityService: CityService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private programService: PspProgramsService,
    private datePipe: DatePipe,
    private myModalService: LookupModalService,
    private filesService: FilesService
  ) { }

  ngOnInit(): void {
    if (this.route.snapshot.params.id) {
      const params = {
        subscriptionId: this.route.snapshot.params.id,
      };
      this.spinner.show();

      this.pspTaskService
        .getRocheSubscriptionById(params)
        .subscribe((response) => {
          this.rocheTask = response.data;

          forkJoin({
            companies: this.companyService.getAllCompanies(),
            cities: this.cityService.getAllCities(),
            programs: this.programService.getAllProgramsByCompanyId(
              PspCompanyEnum.Roche
            ),
          }).subscribe({
            next: (results) => {
              this.companies = results.companies.data?.companies;
              this.cities = [...results.cities];
              this.programList = results.programs.data?.programs;

              this.populateFormData();
              this.loadMedicinesIntoForm();

              this.spinner.hide();
            },
          });
        });
    }
  }

  private loadMedicinesIntoForm(): void {
    if (!this.rocheTask?.medicines || this.rocheTask.medicines.length === 0) {
      return;
    }
    const mappedMedications: MedicationFormValue[] = this.rocheTask.medicines
      .filter((medicine) => medicine.isPspProgram) // only display the medicines that are the psp program
      .map((medicine) => {
        // FIRST: Always include the first titration from the medicine object itself
        const firstTitration = {
          dosage: medicine.dosage || 1,
          frequency: medicine.frequency || 1,
          frequencyDuration: medicine.frequencyDuration || 'يوم',
          period: medicine.period || 1,
          periodDuration: medicine.periodDuration || 'يوم',
          direction: medicine.direction || 'لا يوجد',
        };

        // SECOND: If there are additional titrations in the titration array, append them
        const additionalTitrations =
          medicine.titrations && medicine.titrations.length > 0
            ? medicine.titrations.map((medicineTitration) => ({
              dosage: medicineTitration.dosage,
              frequency: medicineTitration.frequency,
              frequencyDuration: medicineTitration.frequencyDuration,
              period: medicineTitration.period,
              periodDuration: medicineTitration.periodDuration,
              direction: medicineTitration.direction,
            }))
            : [];

        const allTitrations = [firstTitration, ...additionalTitrations];

        return {
          isChronicMedication: medicine.isChronic,
          startDate: undefined,
          endDate: undefined,
          titrations: allTitrations,
          medicineMetadata: medicine.medicineMetadata,
          isRequired: true,
        };
      });

    this.programMedicationsControl.setValue(mappedMedications);
  }

  searchMedications = (
    searchTerm: string
  ): Observable<MedicationSearchResult[]> => {
    if (searchTerm && searchTerm.trim().length > 0) {
      const lowerSearchTerm = searchTerm.toLowerCase();

      const filteredMedications = this.programMedications
        .filter(
          (med) =>
            med.name.toLowerCase().includes(lowerSearchTerm) ||
            (med.genericName &&
              med.genericName.toLowerCase().includes(lowerSearchTerm))
        )
        .map((med) => ({
          label: `${med.name}${med.genericName ? ' - ' + med.genericName : ''}`,
          value: {
            id: med.id,
            name: med.name,
            genericName: med.genericName,
          },
        }));

      return of(filteredMedications);
    }
    return of(this.convertProgramMedicationsToDropdown());
  };
  private convertProgramMedicationsToDropdown(): MedicationSearchResult[] {
    if (!this.programMedications || this.programMedications.length === 0) {
      return [];
    }

    return this.programMedications.map((med) => ({
      label: `${med.name}${med.genericName ? ' - ' + med.genericName : ''}`,
      value: {
        id: med.id,
        name: med.name,
        genericName: med.genericName,
      },
    }));
  }

  private populateFormData(): void {
    if (this.rocheTask) {
      this.companyId = this.rocheTask.program.companyId;
      this.newPatient.programId = this.rocheTask.program.id;
      this.newPatient.patientFullName = this.rocheTask.patient.name;
      this.pspCode = this.rocheTask.patient.pspCode;
      this.subscriptionDate = this.datePipe.transform(
        this.rocheTask.subscriptionDate,
        "'Date:' dd/MM/yyyy 'Time: ' h:mm a"
      );
    }
  }
  openFetchModal(): void {
    this.myModalService.openLookupModal();
  }

  receiveUser(event: PSPPatient): void {
    this.isPreFetched = true;
    this.passportIdToggleOn = false;

    this.user = event;
    this.newPatient.patientFullName = event.fullName;
    this.newPatient.patientMobileNumber = event.phone;
    this.newPatient.patientDOB = new Date(event.birthdate);
    this.patientDateOfBirth = this.datePipe.transform(
      event.birthdate,
      'yyyy-MM-dd'
    );
    this.newPatient.patientCityId = event.cityId;
    this.newPatient.patientNationalId = event.nationalId;
    this.newPatient.patientPassportId = event.passportId;
    this.newPatient.patientAreaId = event.areaId;

    if (event.nationalId === null && event.passportId.length > 0) {
      this.passportIdToggleOn = true;
    }

    this.loadAreas();
  }

  private loadInitialData(): void {
    this.companyService.getAllCompanies().subscribe((response) => {
      this.companies = response.data?.companies;
    });

    this.cityService.getAllCities().subscribe((response) => {
      this.cities = [...response];
    });
    this.loadRochePrograms();
  }

  onCopy(): void {
    const copyText = this.newPatient.patientMobileNumber;
    navigator.clipboard.writeText(copyText);
  }

  onNationalIdToggleChange(): void {
    this.passportIdToggleOn = !this.passportIdToggleOn;

    if (this.passportIdToggleOn) {
      this.newPatient.patientNationalId = null;
    } else {
      this.newPatient.patientPassportId = null;
    }
  }

  onCityChange(event: number): void {
    this.spinner.show();
    this.newPatient.patientCityId = event;
    this.newPatient.patientAreaId = null;
    this.loadAreas();
  }

  onAreaChange(event: number): void { }

  private loadAreas(cityId?: number): void {
    if (!cityId && !this.newPatient.patientCityId) {
      return;
    }

    this.areaService
      .getAreasByCityId(cityId ?? this.newPatient.patientCityId)
      .subscribe((res) => {
        this.areas = [...res.data.areas];
        this.spinner.hide();
      });
  }

  loadRochePrograms(): void {
    this.programService
      .getAllProgramsByCompanyId(PspCompanyEnum.Roche)
      .subscribe((response) => {
        this.programList = response.data?.programs;
      });
  }

  onProgramChange() {
    if (this.newPatient.programId == this.rocheTask.program.id) {
      this.loadMedicinesIntoForm();
    } else if (this.newPatient.programId != this.rocheTask.program.id) {
      this.loadProgramMedications();
      this.programMedicationsControl.setValue([]);
      this.programMedicationsControl.setValue(
        this.programMedications.map((medicine: MedicineMetadata) => ({
          isChronicMedication: false,
          startDate: undefined,
          endDate: undefined,
          medicineMetadata: medicine,
          isRequired: true,
          titrations: [
            {
              dosage: 1,
              frequency: 1,
              frequencyDuration: 'يوم',
              period: 1,
              periodDuration: 'يوم',
              direction: 'لا يوجد',
            },
          ],
        }))
      );
    } else {
      this.programMedications = [];
      this.programMedicationsControl.setValue([]);
    }
  }
  private loadProgramMedications(): void {
    if (!this.newPatient.programId) {
      return;
    }

    this.spinner.show();

    this.pspTaskService
      .getProgramMedicationsAsSearchResults(this.newPatient.programId)
      .subscribe({
        next: (response) => {
          this.spinner.hide();

          if (response.errorList?.length === 0 && response.data) {
            // Check if response.data is an array or single object
            this.programMedications = Array.isArray(response.data)
              ? response.data
              : [response.data];

            this.populateProgramMedicationsIntoForm();
          } else {
            this.programMedications = [];
            this.programMedicationsControl.setValue([]);
            showNotification(
              'warning',
              'No medications found for this program',
              this.toastr
            );
          }
        },
        error: (error) => {
          this.spinner.hide();
          console.error('Error loading program medications:', error);
          this.programMedications = [];
          this.programMedicationsControl.setValue([]);
          showNotification(
            'danger',
            'Error loading program medications',
            this.toastr
          );
        },
      });
  }

  private populateProgramMedicationsIntoForm(): void {
    if (!this.programMedications || this.programMedications.length === 0) {
      this.programMedicationsControl.setValue([]);
      return;
    }

    const mappedMedications: MedicationFormValue[] =
      this.programMedications.map((medicine: MedicineMetadata) => ({
        isChronicMedication: false,
        startDate: undefined,
        endDate: undefined,
        medicineMetadata: medicine,
        isRequired: true,
        titrations: [
          {
            dosage: 1,
            frequency: 1,
            frequencyDuration: 'يوم',
            period: 1,
            periodDuration: 'يوم',
            direction: 'لا يوجد',
          },
        ],
      }));

    // Use setTimeout to ensure change detection picks up the change
    setTimeout(() => {
      this.programMedicationsControl.setValue(mappedMedications);
    }, 0);
  }

  onFileUploaded(files: File[]) {
    for (let i = 0; i < files?.length; i++) {
      let isValidFile = this.isFileValid(files[i]);
      if (isValidFile) {
        this.uploadedDocuments.push(files[i]);
      }
    }
  }

  isFileValid(file: File): boolean {
    let existingFileNames = this.uploadedDocuments?.map((e) => e.name);
    const filesExtensions = this.uploadFileTypes
      .split(',')
      .map((e) => e.trim());
    const fileName = file.name.split('.');
    const fileExtension = fileName[fileName.length - 1];

    let maxDocumentsExceeded =
      this.uploadedDocuments.length >= this.maxDocumentsToUpload;
    if (maxDocumentsExceeded) {
      showNotification(
        'danger',
        `You can upload a maximum of ${this.maxDocumentsToUpload} files. Only the first ${this.maxDocumentsToUpload} files were added.`,
        this.toastr
      );
      return false;
    }

    let isFileNameExist = existingFileNames.find((e) => e == file.name);
    if (isFileNameExist) {
      showNotification(
        'danger',
        `File ${file.name} is already added.`,
        this.toastr
      );
      return false;
    }

    let isValidExtension = filesExtensions.includes(
      `.${fileExtension.toLowerCase()}`
    );
    if (!isValidExtension) {
      showNotification(
        'danger',
        `File format is not supported. Only JPG, JPEG, PNG, GIF and PDF formats are allowed.`,
        this.toastr
      );
      return false;
    }

    return !isFileNameExist && !maxDocumentsExceeded && isValidExtension;
  }

  onSubmit(addPatientForm: NgForm): void {
    if (addPatientForm.valid) {
        // Validate program has drugIndex (Products) assigned to it
        const medications = this.programMedicationsControl.value;
        if (!medications || medications.length === 0 || !medications[0]?.medicineMetadata?.id) {
          showNotification(
            'danger',
            'Program has no Products assigned to it',
            this.toastr
          );
          return;
        }

      const formData = this.changeResponseToFormData(addPatientForm);

      this.pspTaskService
        .ActivateRochePspSubscription(formData)
        .subscribe((response) => {
          if (response.message == 'Success') {
            showNotification('success', response.message, this.toastr);
            setTimeout(() => {
              this.goBack();
            }, 2000);
          } else {
            showNotification('danger', response.message, this.toastr);
          }
        });
    }
  }

  changeResponseToFormData(addPatientForm: NgForm): FormData {
    const formData = new FormData();
    formData.append(
      'subscriptionId',
      this.rocheTask.subscriptionId?.toString() || ''
    );
    formData.append('programId', this.newPatient.programId?.toString() || '');
    formData.append(
      'patientMobileNumber',
      this.newPatient.patientMobileNumber || ''
    );
    formData.append('patientFullName', this.newPatient.patientFullName || '');
    formData.append(
      'patientCityId',
      this.newPatient.patientCityId?.toString() || ''
    );
    formData.append(
      'patientAreaId',
      this.newPatient.patientAreaId?.toString() || ''
    );
    var patientDOB = addPatientForm.value.dob
      ? new Date(addPatientForm.value.dob)
      : new Date(this.user.birthdate);
    formData.append('patientDOB', patientDOB.toDateString() || '');
    formData.append(
      'patientNationalId',
      this.newPatient.patientNationalId || ''
    );
    formData.append(
      'patientPassportId',
      this.newPatient.patientPassportId || ''
    );
    formData.append('pspTypeId', PspTypeEnum.PspPatient.toString() || '');

    for (let i = 0; i < this.uploadedDocuments?.length; i++) {
      formData.append('Files', this.uploadedDocuments[i]);
    }

    formData.append("DrugId", this.programMedicationsControl.value.at(0).medicineMetadata.id.toString())      
    return formData;
  }

  clearDocuments(): void {
    this.uploadedDocuments = [];
  }

  goBack(): void {
    this.router.navigate(['/psp/daily-tasks']);
  }

  viewAttachedRx(task: RocheSubscriptionData, event?: any): void {
    event.preventDefault();
    event.stopPropagation();

    if (task.doctor?.rxUrl) {
      window.open(task.doctor?.rxUrl, '_blank');
    }
  }

  downloadAttachedRx(task: RocheSubscriptionData, event: any): void {
    event.preventDefault();
    event.stopPropagation();
    if (task.doctor?.rxUrl) {
      this.filesService.downloadFile(task.doctor?.rxUrl);
    }
  }
}
